import React, { useState } from "react";
import "./../elementPages.less"; 
import { streamDesc } from "@/config/dataMap";

// 定义类型接口
interface StreamResultProps {
  source: {
    inputStreams: any[];
    outStreams: any[];
  };
}

// 相态中文映射
const phaseMap: Record<string, string> = {
  V: "气相",
  L: "液相",
  VL: "气液混合",
};

// 温度单位选项
const temperatureUnits = ["℃", "K"];

// 压力单位选项
const pressureUnits = ["Pa", "bar", "KPa", "MPa", "atm"];

export default function StreamResult({ source }: StreamResultProps) {
  // 状态管理
  const [temperatureUnit, setTemperatureUnit] = useState("℃");
  const [pressureUnit, setPressureUnit] = useState("MPa");

  // 安全获取流股描述
  const getStreamDescription = (name: string): string => {
    return streamDesc[name as keyof typeof streamDesc] || "";
  };

  // 安全调用 toFixed 方法
  const safeToFixed = (value: any, digits: number = 2): string => {
    if (value === undefined || value === null || typeof value.toFixed !== "function") {
      return "N/A";
    }
    return value.toFixed(digits);
  };

  // 生成表格数据
  const tableData = (data: any[]) => {
    if (!Array.isArray(data)) return [];
    
    return data.map((stream: any) => [
      stream.name || "N/A",
      phaseMap[stream.phase] || stream.phase || "N/A",
      safeToFixed(stream.f_mol_per_h),
      safeToFixed(stream.f_kg_per_h),
      `${Number(safeToFixed(temperatureUnit === "℃" ? stream.T_C : stream.T_K))}`,
      `${Number(safeToFixed(
        {
          Pa: stream.P_Pa,
          bar: stream.P_bar,
          KPa: stream.P_KPa,
          MPa: stream.P_MPa,
          atm: stream.P_atm,
        }[pressureUnit] || 0,
        4,
      ))}`,
      safeToFixed(stream.vf, 4),
      safeToFixed(stream.H),
    ]);
  };

  // 表格表头配置
  const tableHeaders = [
    "流股",
    "相态",
    "摩尔流量（mol/h）",
    "质量流量（kg/h）",
    `温度(${temperatureUnit})`,
    `压力(${pressureUnit})`,
    "气化率",
    "焓（J/mol）",
  ];

  return (
    <div className="stream-result-container">
      {/* 单位选择器 */}
      <div className="unit-selector-container">
        <div className="unit-selector">
          <span className="unit-selector-label">温度单位切换：</span>
          <div className="unit-buttons">
            {temperatureUnits.map((unit) => (
              <button
                key={unit}
                className={`unit-button ${temperatureUnit === unit ? "active" : ""}`}
                onClick={() => setTemperatureUnit(unit)}
              >
                <span className={`unit-button-text ${temperatureUnit === unit ? "active" : ""}`}>
                  {unit}
                </span>
              </button>
            ))}
          </div>
        </div>
        
        <div className="unit-selector pressure-selector">
          <span className="unit-selector-label">压力单位切换：</span>
          <div className="unit-buttons">
            {pressureUnits.map((unit) => (
              <button
                key={unit}
                className={`unit-button ${pressureUnit === unit ? "active" : ""}`}
                onClick={() => setPressureUnit(unit)}
              >
                <span className={`unit-button-text ${pressureUnit === unit ? "active" : ""}`}>
                  {unit}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 表格容器 */}
      <div className="table-container">
        {/* 输入流股表格 */}
        <h3 className="stream-title">输入流股：</h3>
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr className="table-header">
                {tableHeaders.map((header, index) => (
                  <th key={index} className="table-header-cell">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData(source.inputStreams).length > 0 ? (
                tableData(source.inputStreams).map((row, rowIndex) => (
                  <tr key={rowIndex} className="table-row">
                    {row.map((cell, cellIndex) => (
                      <td key={cellIndex} className="table-cell">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr className="empty-row">
                  <td colSpan={tableHeaders.length} className="empty-cell">
                    暂无输入流股数据
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* 输出流股表格 */}
        <h3 className="stream-title">输出流股：</h3>
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr className="table-header">
                {tableHeaders.map((header, index) => (
                  <th key={index} className="table-header-cell">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData(source.outStreams).length > 0 ? (
                tableData(source.outStreams).map((row, rowIndex) => (
                  <tr key={rowIndex} className="table-row">
                    {row.map((cell, cellIndex) => (
                      <td key={cellIndex} className="table-cell">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr className="empty-row">
                  <td colSpan={tableHeaders.length} className="empty-cell">
                    暂无输出流股数据
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}